import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { Ofensiva } from "@noesis/shared";
import { pedir } from "../api.js";
import { useTrilhas } from "./trilhas.js";

/**
 * A ofensiva, disponível fora da home.
 *
 * Ela saiu do painel do dia e foi para a barra lateral, que está em **toda**
 * tela. A home continua sendo quem sabe mais sobre o ritual, mas ela não pode
 * mais ser a única a saber.
 *
 * **Rota própria, e não `home.painel`.** O painel carrega os 365 dias do
 * heatmap; pagá-lo em cada rota do app para desenhar um número na barra seria
 * caro e bobo. `ritual.ofensiva` devolve só a conta.
 *
 * **A home empurra o que já tem.** Quando o painel chega, ele traz a ofensiva
 * junto — e `definir` a entrega aqui sem uma segunda viagem. É o que garante
 * que a barra e o painel nunca discordem sobre o mesmo número, que era o risco
 * de manter duas fontes para o mesmo dado.
 *
 * **Sem trilha ativa a ofensiva é `null`**, e a chama simplesmente não aparece.
 * É estado de produto (RN-04), não erro: a rota devolve `null` em vez de 409
 * justamente para não encher o console de quem ainda não escolheu trilha.
 */

interface Valor {
  ofensiva: Ofensiva | null;
  definir: (o: Ofensiva) => void;
}

const Contexto = createContext<Valor | null>(null);

export function RitualProvider({ children }: { children: ReactNode }) {
  const [ofensiva, setOfensiva] = useState<Ofensiva | null>(null);
  const { estado } = useTrilhas();
  // Trocar de trilha troca a ofensiva (RN-01). É o `ativaId` que dispara a
  // releitura, e não a lista inteira: renomear uma trilha não muda a sequência.
  const ativaId = estado.nome === "pronto" ? estado.ativaId : null;

  useEffect(() => {
    let vivo = true;
    void pedir("ritual.ofensiva")
      .then((r) => {
        if (vivo) setOfensiva(r.ofensiva);
      })
      .catch(() => {
        // A chama é ornamento do ritual, não caminho de tarefa: se a leitura
        // falhar, ela some e o app segue. O invariante 3 vale para a tela que
        // carrega um trabalho, e nenhuma tela depende deste número.
        if (vivo) setOfensiva(null);
      });
    return () => {
      vivo = false;
    };
  }, [ativaId]);

  const definir = useCallback((o: Ofensiva) => setOfensiva(o), []);

  return <Contexto.Provider value={{ ofensiva, definir }}>{children}</Contexto.Provider>;
}

export function useRitual(): Valor {
  const v = useContext(Contexto);
  if (!v) throw new Error("useRitual fora do RitualProvider.");
  return v;
}
